import { Link, Outlet, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Spinner } from "@/components/ui/Spinner";

interface CompanyBranding {
  logoUrl: string | null;
  primaryColor: string;
}

interface PublicCompany {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  /**
   * The operator's IANA timezone. All pickup times in the booking flow are
   * entered and displayed as wall-clock time in this zone (server-provided,
   * validated as a known IANA zone on write).
   */
  timezone: string;
  /**
   * The minimum lead time (hours) the operator requires before a pickup
   * (ONB-U9). Used by the booking funnel to pre-constrain the date picker; the
   * SERVER remains the enforcer. May be absent on older API responses.
   */
  minHoursNotice?: number;
  /**
   * Whether the operator's subscription tier is entitled to the embeddable
   * widget (allowEmbeddableWidget — business_plus/business_ultimate). The
   * chromeless `/widget/:slug` route gates on this (#199); the hosted booking
   * page ignores it. May be absent on older API responses (treated as
   * not-entitled by the widget gate).
   */
  allowEmbeddableWidget?: boolean;
  /**
   * Allowlist of origins that may receive postMessage events from the widget
   * iframe (#458). When non-empty, WidgetLayout uses the first entry as the
   * targetOrigin instead of the wildcard "*". Empty = any origin (legacy).
   */
  allowedEmbedOrigins?: string[];
  branding: CompanyBranding;
}

/**
 * Layout wrapper for the public booking flow.
 * Uses semantic color tokens from the design system.
 * @returns The booking layout with company branding
 */
export function BookingLayout() {
  const { companySlug } = useParams<{ companySlug: string }>();

  const {
    data: company,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["publicCompany", companySlug],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: PublicCompany }>(
        `/public/companies/slug/${companySlug ?? ""}`
      );
      return response.data.data;
    },
    enabled: !!companySlug,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-neutral-900">Business not found</h1>
          <p className="mt-2 text-neutral-600">
            The business you are looking for does not exist or is no longer accepting bookings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header — company logo/name link to the company's public page */}
      <header className="border-b border-neutral-200 bg-surface">
        <div className="mx-auto max-w-3xl px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <Link
              to={`/${companySlug ?? ""}`}
              className="flex min-w-0 items-center space-x-3 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              aria-label={`${company.name} home`}
            >
              {company.branding.logoUrl ? (
                <img
                  src={company.branding.logoUrl}
                  alt={company.name}
                  className="h-10 w-10 rounded-md object-contain"
                />
              ) : (
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md"
                  style={{ backgroundColor: company.branding.primaryColor }}
                >
                  <span className="text-lg font-bold text-white">
                    {company.name.charAt(0)}
                  </span>
                </div>
              )}
              <div className="min-w-0">
                <p className="truncate text-lg font-semibold text-neutral-900">
                  {company.name}
                </p>
                <p className="text-sm text-neutral-600">Book your ride</p>
              </div>
            </Link>

            {company.phone && (
              <a
                href={`tel:${company.phone}`}
                aria-label={`Call ${company.name} at ${company.phone}`}
                className="flex min-h-11 shrink-0 items-center gap-1.5 rounded-sm px-3 text-sm font-medium text-neutral-700 hover:bg-neutral-100 pointer-fine:min-h-9"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M2 3.5A1.5 1.5 0 013.5 2h1.148a1.5 1.5 0 011.465 1.175l.716 3.223a1.5 1.5 0 01-1.052 1.767l-.933.267c-.41.117-.643.555-.48.95a11.542 11.542 0 006.254 6.254c.395.163.833-.07.95-.48l.267-.933a1.5 1.5 0 011.767-1.052l3.223.716A1.5 1.5 0 0118 15.352V16.5a1.5 1.5 0 01-1.5 1.5H15c-1.149 0-2.263-.15-3.326-.43A13.022 13.022 0 012.43 8.326 13.019 13.019 0 012 5V3.5z" />
                </svg>
                <span>{company.phone}</span>
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="mx-auto max-w-3xl px-4 py-4">
        <Outlet context={{ company }} />
      </main>

      {/* Footer — links to RideReserv home */}
      <footer className="border-t border-neutral-200 bg-surface">
        <div className="mx-auto max-w-3xl px-4 py-4">
          <p className="text-center text-sm text-neutral-500">
            <Link
              to="/"
              className="hover:text-neutral-700 hover:underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded-sm"
            >
              Powered by RideReserv
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
}

// Re-export the type for use in child components
export type { PublicCompany };
